/**
 * Supplier Portal Layout - MaintainX Style
 * Enterprise-grade UI with sidebar navigation for supplier dashboard
 */

import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  DollarSign,
  Package,
  Truck,
  Settings,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Menu,
  X,
  Clock,
  CheckCircle,
  Send,
  Building2,
  History,
  Award,
  Megaphone,
  ClipboardList,
  Receipt,
  TrendingUp
} from "lucide-react";
import NotificationCenter from "@/components/NotificationCenter";

// Navigation items for supplier portal
const navItems = [
  { 
    id: "dashboard", 
    label: "Dashboard", 
    icon: LayoutDashboard, 
    path: "/supplier/dashboard",
    exact: true
  },
  { 
    id: "requests", 
    label: "Quote Requests", 
    icon: FileText, 
    path: "/supplier/dashboard?tab=requests" 
  },
  { 
    id: "quotes", 
    label: "My Quotes", 
    icon: DollarSign, 
    path: "/supplier/dashboard?tab=quotes" 
  },
  { type: "divider" },
  { 
    id: "backorders", 
    label: "Backorder Pipeline", 
    icon: Package, 
    path: "/supplier/dashboard?tab=backorders" 
  },
  { 
    id: "campaign", 
    label: "Alert Campaign", 
    icon: Megaphone, 
    isAction: true  // This marks it as an action button, not a navigation link
  },
  { 
    id: "orders", 
    label: "Active Orders", 
    icon: Truck, 
    path: "/supplier/dashboard?tab=orders" 
  },
  { type: "divider" },
  { 
    id: "earnings", 
    label: "Earnings", 
    icon: TrendingUp, 
    path: "/supplier/dashboard?tab=earnings" 
  },
  { 
    id: "invoices", 
    label: "Invoices", 
    icon: Receipt, 
    path: "/supplier/dashboard?tab=invoices" 
  },
  { 
    id: "certification", 
    label: "Certification", 
    icon: Award, 
    path: "/supplier/dashboard?tab=certification" 
  },
  { type: "divider" },
  { 
    id: "activity", 
    label: "Activity Log", 
    icon: History, 
    path: "/supplier/dashboard?tab=activity" 
  },
  { 
    id: "proposals", 
    label: "Project Proposals", 
    icon: ClipboardList, 
    path: "/supplier/dashboard?tab=proposals" 
  },
  { 
    id: "showcase", 
    label: "Vendor Showcase", 
    icon: Award, 
    path: "/vendor-showcase" 
  },
  { type: "divider" },
  { 
    id: "settings", 
    label: "Settings", 
    icon: Settings, 
    path: "/supplier/dashboard?tab=settings" 
  },
];

// Sidebar Component
const Sidebar = ({ collapsed, setCollapsed, supplier, onLogout, onAction, alertCampaignEnabled = true }) => {
  const location = useLocation();

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path.split('?')[0] && !location.search;
    }
    if (item.path.includes('?')) {
      const [path, query] = item.path.split('?');
      return location.pathname === path && location.search.includes(query);
    }
    return location.pathname === item.path;
  };

  const handleItemClick = (item) => {
    if (item.isAction && onAction) {
      onAction(item.id);
    }
  };

  // Filter out Alert Campaign if not enabled
  const filteredNavItems = navItems.filter(item => {
    if (item.id === "campaign" && !alertCampaignEnabled) {
      return false;
    }
    return true;
  });

  return (
    <aside 
      className={`fixed left-0 top-0 h-full bg-slate-900 transition-all duration-300 z-50 flex flex-col ${
        collapsed ? "w-16" : "w-64"
      }`}
      data-testid="supplier-sidebar"
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
        {!collapsed && (
          <Link to="/supplier/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-semibold text-lg">Supplier</span>
          </Link>
        )}
        {collapsed && (
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mx-auto">
            <Truck className="w-5 h-5 text-white" />
          </div>
        )}
        <button 
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-400 hover:text-white p-1 rounded hidden lg:block"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {filteredNavItems.map((item, index) => {
            if (item.type === "divider") {
              return (
                <li key={`divider-${index}`} className="my-3">
                  <div className="h-px bg-slate-800 mx-2" />
                </li>
              );
            }

            const Icon = item.icon;
            const active = !item.isAction && isActive(item);

            // Handle action items (like Campaign Builder)
            if (item.isAction) {
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleItemClick(item)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group w-full text-amber-400 hover:bg-amber-500/20 hover:text-amber-300"
                    title={collapsed ? item.label : undefined}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    )}
                  </button>
                </li>
              );
            }

            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors group ${
                    active
                      ? "bg-blue-600 text-white"
                      : "text-slate-400 hover:bg-slate-800 hover:text-white"
                  }`}
                  title={collapsed ? item.label : undefined}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" />
                  {!collapsed && (
                    <span className="text-sm font-medium truncate">{item.label}</span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile & Logout */}
      <div className="border-t border-slate-800 p-3">
        {!collapsed ? (
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 bg-slate-700 rounded-full flex items-center justify-center">
              <Building2 size={18} className="text-slate-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {supplier?.company_name || supplier?.name || "Supplier"}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {supplier?.contact_name || supplier?.email || ""}
              </p>
            </div>
          </div>
        ) : null}
        <button
          onClick={onLogout}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors ${
            collapsed ? "justify-center" : ""
          }`}
          title="Logout"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

// Top Header Component
const TopHeader = ({ title, subtitle, actions, collapsed, token }) => {
  return (
    <header 
      className={`sticky top-0 z-40 bg-white border-b border-slate-200 h-14 sm:h-16 flex items-center justify-between px-4 sm:px-6 transition-all duration-300 ${
        collapsed ? "lg:ml-16" : "lg:ml-64"
      }`}
      data-testid="top-header"
    >
      <div className="ml-10 lg:ml-0">
        <h1 className="text-lg sm:text-xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs sm:text-sm text-slate-500 hidden sm:block">{subtitle}</p>}
      </div>
      
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Notification Center */}
        <NotificationCenter 
          token={token} 
          userType="supplier"
          onNotificationClick={(notification) => {
            console.log("Notification clicked:", notification);
          }}
        />
        
        {/* Custom Actions */}
        {actions}
      </div>
    </header>
  );
};

// Main Layout Export
export default function SupplierLayout({ 
  children, 
  title = "Dashboard", 
  subtitle,
  actions,
  supplier = null,
  onLogout = () => {},
  onAction = () => {},
  alertCampaignEnabled = true,
  token = null
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get token from localStorage if not provided
  const authToken = token || localStorage.getItem("supplier_token");

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Menu Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Hidden on mobile unless menu open */}
      <div className={`lg:block ${mobileMenuOpen ? "block" : "hidden"}`}>
        <Sidebar 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          supplier={supplier}
          onLogout={onLogout}
          onAction={onAction}
          alertCampaignEnabled={alertCampaignEnabled}
        />
      </div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:ml-16" : "lg:ml-64"}`}>
        <TopHeader 
          title={title} 
          subtitle={subtitle}
          actions={actions}
          collapsed={collapsed}
          token={authToken}
        />
        
        <main className="p-4 sm:p-6" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export { Sidebar, TopHeader };
